import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, CalendarCheck, Calendar, Building2, Shield
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";

const AdminDashboard = () => {
  const { user } = useAuth();

  const [stats, setStats] = useState({
    totalVolunteers: 0,
    totalColleges: 0,
    totalEvents: 0,
    pendingAttendance: 0,
    todayPresent: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const [volRes, colRes, eventRes, pendingRes, presentRes] = await Promise.all([
        supabase.from("volunteers").select("id", { count: "exact", head: true }),
        supabase.from("colleges").select("id", { count: "exact", head: true }),
        supabase.from("events").select("id", { count: "exact", head: true }),
        supabase.from("attendance_log").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase
          .from("attendance_log")
          .select("id", { count: "exact", head: true })
          .eq("status", "present")
          .gte("login_time", new Date().toISOString().slice(0, 10) + "T00:00:00"),
      ]);

      setStats({
        totalVolunteers: volRes.count || 0,
        totalColleges: colRes.count || 0,
        totalEvents: eventRes.count || 0,
        pendingAttendance: pendingRes.count || 0,
        todayPresent: presentRes.count || 0,
      });
      setLoadingStats(false);
    };

    fetchStats();
  }, []);

  const summaryCards = [
    {
      label: "Total Volunteers",
      value: stats.totalVolunteers,
      icon: Users,
      color: "from-violet-500 to-purple-600",
      link: "/admin/students",
    },
    {
      label: "Colleges",
      value: stats.totalColleges,
      icon: Building2,
      color: "from-emerald-500 to-teal-500",
      link: "/admin/colleges",
    },
    {
      label: "Total Events",
      value: stats.totalEvents,
      icon: Calendar,
      color: "from-fuchsia-500 to-pink-600",
      link: "/admin/events",
    },
    {
      label: "Pending Attendance",
      value: stats.pendingAttendance,
      icon: CalendarCheck,
      color: "from-amber-500 to-orange-500",
      link: "/admin/attendance",
    },
  ];

  const quickLinks = [
    {
      title: "Manage Events",
      description: "Create drives, schedule date & time, assign to colleges, and view rosters.",
      icon: Calendar,
      link: "/admin/events",
      gradient: "from-fuchsia-600 to-pink-600",
    },
    {
      title: "Manage Attendance",
      description: "Mark volunteers as present or absent by college and event in real time.",
      icon: CalendarCheck,
      link: "/admin/attendance",
      gradient: "from-purple-600 to-violet-600",
    },
    {
      title: "Student Management",
      description: "Add, edit, or remove volunteers and export Excel rosters.",
      icon: Users,
      link: "/admin/students",
      gradient: "from-emerald-600 to-teal-600",
    },
    {
      title: "College Management",
      description: "Manage colleges, view student counts, and organize by institution.",
      icon: Building2,
      link: "/admin/colleges",
      gradient: "from-amber-600 to-orange-600",
    },
  ];

  return (
    <div className="min-h-screen bg-ku-cream">
      {/* Header */}
      <section className="relative w-full py-10 md:py-14 bg-gradient-to-br from-[#1a1a2e] via-[#2d1b4e] to-[#3B1E60] text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-ku-gold/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ku-gold/20 border border-ku-gold/30 text-xs font-bold uppercase tracking-widest text-ku-gold mb-3">
                <Shield className="w-3.5 h-3.5" />
                Admin Panel
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-tight">
                Admin Dashboard
              </h1>
              <p className="text-white/60 text-sm md:text-base mt-1 font-medium">
                Welcome, {user?.name || "Admin"} — Manage events, volunteers, attendance, and colleges.
              </p>
            </motion.div>

            
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="relative w-full pt-8 pb-8">
        <div className="w-full max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between gap-4 mb-5">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-ku-violet">Workspace</p>
              <h2 className="text-2xl font-black text-ku-purple tracking-tight mt-1">Quick Actions</h2>
            </div>
      
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {quickLinks.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
              >
                <Link
                  to={item.link}
                  className="block bg-white rounded-2xl p-6 shadow-card border border-ku-soft/50 hover:shadow-elevated hover:-translate-y-1 transition-all duration-300 no-underline group h-full min-h-[230px] flex flex-col justify-between"
                >
                  <div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-black text-ku-purple">{item.title}</h3>
                    <p className="text-sm text-gray-500 mt-2 font-medium leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="relative w-full pb-12">
        <div className="w-full max-w-6xl mx-auto px-6">
          <div className="mb-5">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-ku-violet">At A Glance</p>
            <h2 className="text-2xl font-black text-ku-purple tracking-tight mt-1">Statistics</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {summaryCards.map((card, index) => (
              <motion.div
                key={card.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <Link
                  to={card.link}
                  className="block bg-white rounded-2xl p-5 shadow-card border border-ku-soft/50 hover:shadow-elevated hover:-translate-y-1 transition-all duration-300 no-underline h-full min-h-[185px]"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-3`}>
                    <card.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-2xl md:text-3xl font-black text-ku-purple tracking-tight">
                    {loadingStats ? (
                      <span className="inline-block w-10 h-8 bg-gray-200 rounded animate-pulse" />
                    ) : (
                      card.value
                    )}
                  </div>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mt-1">
                    {card.label}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;